import React, { useState, useEffect } from 'react';
import { dossiersListConfigAPI } from '../../services/api';
import { 
  TableProperties, Eye, EyeOff, GripVertical, Save, 
  RefreshCw, ChevronUp, ChevronDown, Check
} from 'lucide-react';

const WidgetDossiersListConfig = () => {
  const [columns, setColumns] = useState([]);
  const [availableColumns, setAvailableColumns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    setLoading(true);
    try {
      const res = await dossiersListConfigAPI.getColumns();
      // Trier les colonnes par ordre
      const sortedColumns = [...res.data.columns].sort((a, b) => a.ordre - b.ordre);
      setColumns(sortedColumns);
      setAvailableColumns(res.data.available_columns || []);
    } catch (error) {
      console.error('Erreur chargement config:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleColumn = (code) => {
    setColumns(columns.map(col => 
      col.code === code ? { ...col, visible: !col.visible } : col
    ));
    setHasChanges(true);
  };

  const moveColumn = (index, direction) => {
    const newColumns = [...columns];
    const newIndex = index + direction;
    
    if (newIndex < 0 || newIndex >= columns.length) return;
    
    // Échanger les éléments
    [newColumns[index], newColumns[newIndex]] = [newColumns[newIndex], newColumns[index]];
    
    // Mettre à jour les ordres
    newColumns.forEach((col, i) => {
      col.ordre = i;
    });
    
    setColumns(newColumns);
    setHasChanges(true);
  };

  const saveConfig = async () => {
    setSaving(true);
    try {
      await dossiersListConfigAPI.updateColumns(columns);
      setHasChanges(false);
    } catch (error) {
      console.error('Erreur sauvegarde:', error);
      alert('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const resetConfig = async () => {
    if (!window.confirm('Réinitialiser la configuration des colonnes par défaut ?')) return;
    try {
      await dossiersListConfigAPI.resetColumns();
      await loadConfig();
      setHasChanges(false);
    } catch (error) {
      console.error('Erreur reset:', error);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
      </div>
    );
  }

  const visibleCount = columns.filter(c => c.visible).length;
  const hiddenCount = columns.filter(c => !c.visible).length;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
            <TableProperties className="w-6 h-6 text-green-600" />
            Configuration du tableau des dossiers
          </h2>
          <p className="text-sm text-gray-600 mt-1">
            Choisissez et réorganisez les colonnes affichées dans la liste des dossiers
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={resetConfig}
            className="text-sm text-gray-500 hover:text-gray-700 flex items-center gap-1 px-3 py-2"
          >
            <RefreshCw className="w-4 h-4" />
            Réinitialiser
          </button>
          {hasChanges && (
            <button
              onClick={saveConfig}
              disabled={saving}
              className="px-4 py-2 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 disabled:opacity-50 flex items-center gap-2"
              data-testid="save-columns-config"
            >
              {saving ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              Enregistrer
            </button>
          )}
        </div>
      </div>

      {/* Explication */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm text-blue-800">
        <p>
          <strong>Mode d'emploi :</strong> Utilisez les flèches pour réorganiser l'ordre des colonnes. 
          Cliquez sur l'icône œil pour afficher/masquer une colonne. 
          Les modifications s'appliquent à tous les utilisateurs.
        </p>
      </div>

      {/* Liste des colonnes */}
      <div className="space-y-2">
        {columns.map((col, index) => (
          <div
            key={col.code}
            className={`bg-white rounded-lg border-2 transition ${
              col.visible 
                ? 'border-green-200 shadow-sm' 
                : 'border-gray-200 opacity-60'
            }`}
          >
            <div className="p-4 flex items-center gap-4">
              {/* Contrôles d'ordre */}
              <div className="flex flex-col items-center gap-1">
                <button
                  onClick={() => moveColumn(index, -1)}
                  disabled={index === 0}
                  className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30"
                  title="Monter"
                >
                  <ChevronUp className="w-4 h-4" />
                </button>
                <GripVertical className="w-5 h-5 text-gray-300" />
                <button
                  onClick={() => moveColumn(index, 1)}
                  disabled={index === columns.length - 1}
                  className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30"
                  title="Descendre"
                >
                  <ChevronDown className="w-4 h-4" />
                </button>
              </div>

              {/* Numéro d'ordre */}
              <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center text-sm font-medium text-gray-600">
                {index + 1}
              </div>

              {/* Infos colonne */}
              <div className="flex-1">
                <h3 className="font-semibold text-gray-900">{col.label}</h3>
                <p className="text-sm text-gray-500">Code : {col.code}</p>
              </div>

              {/* Toggle visibilité */}
              <button
                onClick={() => toggleColumn(col.code)}
                className={`p-3 rounded-lg transition ${
                  col.visible 
                    ? 'bg-green-100 text-green-600 hover:bg-green-200' 
                    : 'bg-gray-100 text-gray-400 hover:bg-gray-200'
                }`}
                title={col.visible ? 'Masquer cette colonne' : 'Afficher cette colonne'}
                data-testid={`toggle-column-${col.code}`}
              >
                {col.visible ? <Eye className="w-5 h-5" /> : <EyeOff className="w-5 h-5" />}
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Stats */}
      <div className="bg-gray-50 rounded-lg p-4 text-sm text-gray-600">
        <div className="flex items-center gap-4">
          <span className="flex items-center gap-1">
            <Eye className="w-4 h-4 text-green-600" />
            <strong>{visibleCount}</strong> colonnes visibles
          </span>
          <span>•</span>
          <span className="flex items-center gap-1">
            <EyeOff className="w-4 h-4 text-gray-400" />
            <strong>{hiddenCount}</strong> colonnes masquées
          </span>
        </div>
      </div>

      {/* Avertissement changements non sauvegardés */}
      {hasChanges && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-center justify-between">
          <p className="text-amber-800 text-sm font-medium">
            Vous avez des modifications non enregistrées
          </p>
          <button
            onClick={saveConfig}
            disabled={saving}
            className="px-4 py-2 bg-amber-600 text-white rounded-lg font-medium hover:bg-amber-700 disabled:opacity-50 flex items-center gap-2"
          >
            {saving ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
            Enregistrer maintenant
          </button>
        </div>
      )}
    </div>
  );
};

export default WidgetDossiersListConfig;
